import type { HTMLAttributes } from 'react';

import { ChevronDown } from '@remarkable/ark-icons';
import { Label } from '@remarkable/ark-web';
import { useFormContext } from 'react-hook-form';

import { CA_PROVINCES, US_STATES } from 'src/utils/countryList';

import { cn } from '../utils/classNamesHelper';

interface StatePickerProps {
  disabled?: boolean;
  name?: string;
  isEditable?: boolean;
  countryFieldName?: string;
  country?: string;
}

export const StatePicker = ({
  name = 'state',
  countryFieldName = 'country',
  isEditable = true,
  disabled = false,
  country,
  ...props
}: HTMLAttributes<HTMLDivElement> & StatePickerProps) => {
  const { register, watch, formState } = useFormContext();
  const countryCode = country ?? (watch(countryFieldName) as string);
  const value = watch(name) as string;
  const defaultValue = formState.defaultValues?.[name] as string;

  const states: Record<string, typeof US_STATES | typeof CA_PROVINCES> = {
    US: US_STATES,
    CA: CA_PROVINCES,
  };
  const activeStates = states[countryCode] ?? [];
  const isCanada = countryCode === 'CA';

  const displayValue = activeStates.find((c) => c.value === value)?.label;

  if (countryCode in states === false) {
    return null;
  }

  return (
    <div {...props}>
      <Label htmlFor={name}>{isCanada ? 'Province' : 'State'}</Label>
      {isEditable ? (
        <div className={cn('rounded-sm relative block')}>
          <select
            className="rounded-sm h-48 w-full cursor-pointer appearance-none border bg-none px-16 py-8 shadow-border-muted"
            id={name}
            {...register(name, {
              required: true,
            })}
            defaultValue={defaultValue}
            disabled={disabled}
          >
            {!value && (
              <option value="">
                {isCanada ? 'Choose province...' : 'Choose state...'}
              </option>
            )}

            {activeStates.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={32}
            className="pointer-events-none absolute top-0 right-12 h-full text-pen-blue"
          />
        </div>
      ) : (
        <p className="min-h-[16px]">{displayValue ?? '-'}</p>
      )}
    </div>
  );
};
